import React, {useState, useRef, useEffect, useMemo, Fragment} from 'react';
import Link from 'next/link';
import { v4 as uuidv4 } from 'uuid';
import {useRouter} from 'next/router';
import {Loading,FieldDropdown, TextHighlight, DesktopRegisterHeader, UserIcon, VisDiv, ViewLabelValueField, Divider} from '..';
import {useStateContext} from '../../context/StateContext';
import { unique, clientName, isDeviceChecked, isDeviceSevere, deviceSubstitutionStatus, offerTotals, siteName, euro, percentage, errorAlert, allArrayDataInTargetArray, registerIdsFromOfferFields } from '../../lib/utils'; 
import {useCreateReplace, useDeleteDocument} from '../../hooks/mutations'
import Swal from "sweetalert2"
import { IoIosAddCircleOutline,  IoIosEye, IoIosEyeOff, IoMdCart  } from 'react-icons/io';
import PromiseQueue from 'p-queue'
import TrueFalseToggle from '../TrueFalseToggle';
import { useExpanded, useTable, useColumnOrder, useSortBy } from "react-table";
import { useQuery, useQueries } from '@tanstack/react-query';
import useMediaQuery from '../../hooks/useMediaQuery';
import deadlines from '../Deadlines';
import { referenceDoc } from '../gestioneDatabase/Docs';
import client from '../../sanity_ecommerce/schemas/client';
import { HiddenOfferData } from './HiddenOfferData';
import TestButton from '../frontend_components/TestButton';

/**
 * A component that displays a summary of offers including client details, risk assessments, and financial data
 * #offer #offerTotals
 * @param {Object} props - Component props
 * @param {Object} props.data - The data object containing all necessary information
 * @param {Array} props.data.devices - Array of devices with their status and details
 * @param {Array} props.data.sites - Array of sites with their information
 * @param {Array} props.data.registers - Array of registers associated with the sites
 * @param {Array} props.data.nonConformities - Array of non-conformity definitions
 * @param {Object} props.data.client - Client information object
 * @param {Array} props.data.deviceNonConformities - Array of device-specific non-conformities
 * @param {Array} props.data.registerTemplates - Array of register templates defining structure
 * @param {Array} props.data.offerData - Current offer data array for calculations
 * @param {Array} props.data.generatedOfferData - Generated offer data for display
 * @param {Array} props.data.allGeneratedOfferData - Complete generated offer data including hidden items
 * @param {Function} props.showHide - Callback function to toggle visibility of offer items, it takes an array of offer items
 * @returns {JSX.Element} A summary view of offers with client details, risk metrics, and financial data
 */
const OfferSummary = ({data={}, showHide=()=>{}}) => {
    const {devices=[], clientOrder, allOffers, sites, registers, nonConformities, client, deviceNonConformities, registerTemplates, offerData, generatedOfferData, allGeneratedOfferData} = data
    const { sUser, offer, setOffer} = useStateContext();
    //const devices = devices?.length > 0 ? devices : []
    const [loading, setLoading] = useState(true)
    const dataTypes = [
      {label:"PREZZO", value:"PREZZO", _key:1},
      {label:"MARGINE", value:"MARGINE",_key:2},
      {label:"COSTO", value:"COSTO",_key:3},
    ]
    const [workingOfferData, setWorkingOfferData] = useState()

    const router = useRouter()
    
    const onCellClick = (site, register, template) => { 
      console.log("cell clicked",register, template, site)

  }
    
    const [dataType, setDataType] = useState({label:"PREZZO", value:"PREZZO"})
    const [workingData, setWorkingData] = useState([])

    
    //DETAILS DATA
    //#reports
    const risk = (value) =>{
        if(value == 0) return {color: "green", riskLevel: "ASSENTE"}
        if(value <= 0.3) return {color: "yellow", riskLevel: "MODERATO"}
        if(value <= 0.6 ) return {color: "amber", riskLevel: "MEDIO"}
        if(value < 1 ) return {color: "red", riskLevel: "ALTO"}
        if(value >= 1 ) return {color: "darkRed", riskLevel: "CRITICO"}
    }

    const nPlessi = !Array.isArray(sites) ? 0 : sites?.filter?.(x=> x?.status!=="ARCHIVIATO").length
    const nRegisters = !Array.isArray(registers) ? 0 : registers?.filter?.(x=> x?.status!=="ARCHIVIATO").length
    const nPresidi = !Array.isArray(devices) ? 0 : devices?.filter?.(x=> x?.status!=="ARCHIVIATO" && x?.status!=="DA FORNIRE")?.length
    const nNonConformita = !Array.isArray(deviceNonConformities) ? 0 : deviceNonConformities?.filter?.(x=> x?.status!=="ARCHIVIATO").length
    const riskRatio = () =>{ 
        const calc = (nNonConformita/nPresidi)
        const value = parseFloat(calc).toFixed(1)

        return ({value, color: risk(value)?.color, riskLevel:risk(value)?.riskLevel})
        }
    
    const largeScreen = useMediaQuery("(min-width: 1253px)")
    const mediumScreen = useMediaQuery("(min-width: 925px)")
    const ipadScreen = useMediaQuery("(min-width: 725px)")

    const labelValueDistance = () => {
        if(mediumScreen) return 4
        if(ipadScreen) return 2
        return 1
    } 

    const clientDetails = (
        <div style={{textAlign:"left"}}>
        <ViewLabelValueField label={"PLESSI"} value={nPlessi}highlight={"blue"}labelValueDistance = {labelValueDistance()}/>
        <ViewLabelValueField label={"REGISTRI"} value={nRegisters}highlight={"blue"}labelValueDistance = {labelValueDistance()}/>
        <ViewLabelValueField label={"PRESIDI"} value={nPresidi}highlight={"blue"}labelValueDistance = {labelValueDistance()}/>
        <ViewLabelValueField label={"NON CONFORMITA'"} value={nNonConformita}highlight={riskRatio()?.color}labelValueDistance = {labelValueDistance()}/>
        <ViewLabelValueField label={"COSTANTE DI RISCHIO"} value={riskRatio()?.value}highlight={riskRatio()?.color}labelValueDistance = {labelValueDistance()}/>
        <ViewLabelValueField label={"LIVELLO DI RISCHIO"} value={riskRatio()?.riskLevel}highlight={riskRatio()?.color}labelValueDistance = {labelValueDistance()}/>
        <p>La costante di rischio è il rapporto tra il numero di presidi e il numero di non conformità. il livello di rischio viene calcolato come segue:</p>
        <div style={{marginBottom:"15px"}}>
            <Legenda label="ASSENTE" color="green" description="Se la costante è 0" />
            <Legenda label="MODERATO" color="yellow" description="Se la costante è minore di 0.3" />
            <Legenda label="MEDIO" color="amber" description="Se è compresa fra 0.3 e 0.6" />
            <Legenda label="ALTO" color="red" description="Se è compresa fra 0.6 e 1" />
            <Legenda label="CRITICO" color="darkRed" description="Se è maggiore di 1" />
                   
        </div>
    
        </div>
    )

    useEffect(()=>{
      setLoading(true)
      //setDataType({label:"PREZZO", value:"PREZZO"})

     // console.log("generatedOfferData", generatedOfferData,"offerdata", offerData,)
      if(offer){
        setWorkingOfferData(generatedOfferData)
        //console.log("lnvlajnclakndc", data)
        setWorkingData(siteList(data, generatedOfferData, dataType))
        
      } else {
        setWorkingOfferData(offerData)
        setWorkingData(siteList(data, offerData, dataType))
      }
     
      //console.log("COLUMNS",columns, "DATA", data, )
      setLoading(false)
  },[sites, client, dataType, offer, offerData, generatedOfferData])


    const formattedValue = (header, value) =>{
        if(header == "PLESSO") return value
        if(dataType == "MARGINE") return value ? percentage(value): ""
        return value ? euro(value) : ""
    }


  const consoleData = {
    ...data,
    findRegisterData: data?.offerData?.filter((x)=>x?.deviceId == "f7056bdd-5b39-4928-9164-997290f538e5"),
  }
/* 
    const tableValues = !workingData ? [] : workingData?.map?.((x)=> x?.reduce((prevItem, item)=>{
      const exists = !!prevItem[item.accessor]
      const value = () => {
        if (exists) {return prevItem[item.accessor]+item.value}
        return item.value
      }
      return({...prevItem, [item.accessor]: value()})
    },{})
    )

    const uniqueTableDataArray = unique("accessor",workingData?.flat(2))

    const tableColumns = uniqueTableDataArray.map?.((item => {
      const order = registerTemplates?.find?.(x=>x._id==item?.accessor)?.order
      const values = workingData?.flat(2)?.filter?.((x)=>x?.accessor == item?.accessor)?.map?.((item)=>+item?.value)
      const sum = values?.reduce((partialSum, a) => partialSum + a, 0)?.toFixed(2)
      const footerTotal = () => {
        //if(sum && item?.accessor == "totals") return `TOTALE COMPLESSIVO: ${+sum}`
        if(sum) return sum 
        return 0
      } 
      return({
        accessor: item?.accessor,
        Header: item?.Header,
        siteNumber: item?.siteNumber,
        order: order,
        Footer: item?.Footer ? item?.Footer : footerTotal()

//==========================================================================================================
    })})) */
    /**
     * 
     * @param {*} param0 
     * @returns an array of the offers to update
     */
  
/*     const updateOfferData = ({registerTemplateId, siteId, allGeneratedOfferData, allOffers, clientOrder }) => {
        const data = dataFilter({allGeneratedOfferData, registerTemplateId, siteId})
        const {filteredData=[], allProductsHidden, allProductsClientOrder} = data
        const siteIds = unique("siteId", filteredData)?.map?.(x=>x?.siteId)
        const offersToUpdate = !Array.isArray(allOffers) ? [] : allOffers?.filter?.((x)=>siteIds?.includes(x?.site?._ref))
        const toggleAllData = (offers, bool) => {
          const productsToToggle = offers?.map?.((offer)=>{
            const newHideOffer = {...offer, offerFields: offer?.offerFields?.map?.((item)=>({...item, hide: bool}))}
            const newClientOrder = {...offer, offerFields: offer?.offerFields?.map?.((item)=>({...item, clientOrder: bool ? referenceDoc(clientOrder?._id) : undefined}))}
            return clientOrder ? newClientOrder : newHideOffer
          })
          return productsToToggle
        }
        const toggleRegisterTemplateData = (offers, bool, targetData) => {
          const productsToToggle = offers?.map?.((offer)=>{
            const filteredTargetData = !Array.isArray(targetData) ? [] : targetData?.filter?.((x)=>x?.siteId == offer?.site?._ref)
            const newOfferFields = offer?.offerFields?.map?.((item)=>{ 
              const isFieldInFilteredTargetData = filteredTargetData?.some?.((x)=>x?._id == item?._id)
              if(!isFieldInFilteredTargetData) return item
              const newHideDoc = {...item, hide: bool}
              const newClientOrderDoc = {...item, clientOrder: bool ? referenceDoc(clientOrder?._id) : undefined}
              return clientOrder ? newClientOrderDoc : newHideDoc
            })
            //const newOfferFields = [...offer?.offerFields?.filter?.((x)=>!filteredTargetData?.some?.((y)=>y?._id == x?._id)), ...filteredTargetData]
            const newOffer = {...offer, offerFields: newOfferFields}
            return newOffer
          })
          return productsToToggle
        }

        if(registerTemplateId == "site"){
          if(allProductsHidden && !clientOrder) { return toggleAllData(offersToUpdate, false) }
          if(!allProductsHidden && !clientOrder) { return toggleAllData(offersToUpdate, true) }
          if (allProductsClientOrder && clientOrder) { return toggleAllData(offersToUpdate, false) }
          if (!allProductsClientOrder && clientOrder) { return toggleAllData(offersToUpdate, true) }
        }
        //return ({registerTemplateId, siteId, allGeneratedOfferData, filteredData, allProductsHidden, allOffers, offersToUpdate, siteIds})
        else {
          if(allProductsHidden && !clientOrder) { return toggleRegisterTemplateData(offersToUpdate, false, filteredData) }
          if(!allProductsHidden && !clientOrder) { return toggleRegisterTemplateData(offersToUpdate, true, filteredData) }
          if(allProductsClientOrder && clientOrder) { return toggleRegisterTemplateData(offersToUpdate, false, filteredData) }
          if(!allProductsClientOrder && clientOrder) { return toggleRegisterTemplateData(offersToUpdate, true, filteredData) }
        }
      } */
//==========================================================================================================
    const tableData = summaryTableData(registerTemplates, workingData) 
    

    function compare( a, b ) {
      if ( a?.order < b?.order ){
        return -1;
      }
      if ( a?.order > b?.order ){
        return 1;
      }
      return 0;
    }
    const valuesData = useMemo(() => (tableData?.data),[workingData]);

    const columns = useMemo(
      () => (tableData?.columns),[workingData]
    );


    const changeOrder = () => {
      const array = columns?.map?.((item)=>{
          const order = () => {
            if(item.accessor == "site") return 1
            if(item.accessor == "signs") return 9999
            if(item.accessor == "totals") return 10000
            return item?.order
          }
        return(
        {
          ...item,
          order: order()
        }
      )})
      .sort(compare)
      .map?.(x=> x?.accessor)
      setColumnOrder(array)
    }
    
    useEffect(()=>{
      changeOrder()
      setLoading(false)
    },[valuesData])



    const { getTableProps, getTableBodyProps, footerGroups, headerGroups, rows, prepareRow, setColumnOrder } = 
    useTable({ 
      columns , 
      data: valuesData,
      initialState: {
        sortBy: [
            {
                id: 'site',
                desc: false
            }
        ]
    }    
    },
    useSortBy,
    useColumnOrder, 
    );

    const filterOtherOrders = (x) => !x?.clientOrder?._ref || x?.clientOrder?._ref != clientOrder?._id
    const registerTemplateIdWithSigns = (item) => 
      { if(item?.isSign) return "weduwheiudghyegwuyjke88363ghdloiejdf87"
        return item?.registerTemplateId 
      }

    if (loading)return <Loading/>
        
  return (
    <div className="App"  style={{overflowX:"scroll" }}>
    
     {clientDetails}
     <TestButton consoleData={consoleData} buttonId={"kdjxqolixjqwoixj"} message={"test summary"} />
     <Divider/>
     <VisDiv rules={[true]} style={{display:"flex", alignItems:"center", flexDirection:"column"}}>
        <h2>  </h2>
        {/* <h2>{clientName(client)}</h2> */}
     </VisDiv>
     <div style={{marginTop:"10px"}}>
        <h4>SELEZIONA DATO DA VISUALIZZARE</h4>
     </div>
      <FieldDropdown
        //key={client._key}
        initialValue={dataType}
        isSearchable="isSearchable"
        placeHolder="Seleziona..."
        options={dataTypes}
        onChange={(value) => {setDataType(value)
        }}
      />
      <div className="container" style={{overflowX:"scroll" }}>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map?.((headerGroup, index) => (
              <tr  key={index} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map?.((column, index) => {
  
                      
                  const totalsBackground = column?.Header == "TOTALI" ? {backgroundColor:"grey", color: "white"} : {}
                  const headerTitle = column.render("Header")
                  const registerTemplateId = column?.id == "signs" ? "weduwheiudghyegwuyjke88363ghdloiejdf87" : column?.id 
                  
                  // these are all the register ids in the offer fields including sign "weduwheiudghyegwuyjke88363ghdloiejdf87"
                  const existingRegistersIds = registerIdsFromOfferFields(offerData) 
                  const isThereSignRegister = existingRegistersIds?.some?.((x=>x=="weduwheiudghyegwuyjke88363ghdloiejdf87"))
                  //console.log("kwndkjwnckwejcnakwejnc", registers)
                 // const registersWithRegisterTemplateId =!Array.isArray(registers) ? [] : registers?.filter?.((x)=>x?.template_ref == registerTemplateId)
                 const registersWithRegisterTemplateId =!Array.isArray(registers) ? [] : 
                    isThereSignRegister && registerTemplateId == "weduwheiudghyegwuyjke88363ghdloiejdf87" ?
                      [  
                        {
                          label: "SEGNALETICA",
                          //site:{_type:"reference", _key:keyId(), _ref: site?._id},
                          _id: "weduwheiudghyegwuyjke88363ghdloiejdf87",
                          _type: "register",
                          status: "ATTIVO", 
                          template:{_type:"reference",  _ref: "weduwheiudghyegwuyjke88363ghdloiejdf87"},
                      }]
                      : registers?.filter?.((x)=>x?.template?._ref == registerTemplateId) 
                  //console.log("lsclsdcjsdlj",{registerTemplateId, registers, headerTitle, registersWithRegisterTemplateId, existingRegistersIds, offerFields: offerData})
/*                   const registersWithRegisterTemplateId =!Array.isArray(registers) ? [] : 
                   registers?.filter?.((x)=>x?.template_ref == registerTemplateId) */
                  
                  const areAllFamiliesHidden = 
                    !Array.isArray(allOffers) ? false : 
                      allOffers?.every?.((offer)=>!Array.isArray(offer?.hideRegisters) ? false : allArrayDataInTargetArray({sourceArray: offer?.hideRegisters, targetArray: registersWithRegisterTemplateId, targetKey: "_id"}))
                  
                  const areAllOffersHidden = !Array.isArray(allOffers) ? false : allOffers?.every?.((offer)=>{
                     
                    const hiddenRegisters = offer?.hideRegisters
                    const areAllHidden = allArrayDataInTargetArray({sourceArray: hiddenRegisters, targetArray: existingRegistersIds})
                    return areAllHidden
                  })
/*                   if(registerTemplateId == "weduwheiudghyegwuyjke88363ghdloiejdf87") console.log("lsclsdcjsdljc signs",
                    {areAllFamiliesHidden, allOffers,registerTemplateId, areAllOffersHidden, registersWithRegisterTemplateId, existingRegistersIds}) */

                  const areAllFamiliesOrdered = !Array.isArray(allOffers) ? false : allOffers
                    ?.every?.((offer)=>!Array.isArray(offer?.offerFields) ? false : HiddenOfferData({offerData: offer?.offerFields, allOffers})
                      ?.filter?.(x=>registerTemplateIdWithSigns(x) == registerTemplateId && !x?.hide || (x?.clientOrder?._ref && x?.clientOrder?._ref != clientOrder?._id))
                      ?.every?.((x)=>x?.clientOrder?._ref))
                  const areAllOffersOrdered = !Array.isArray(allOffers) ? false : allOffers
                    ?.map?.((offer)=>!Array.isArray(offer?.offerFields) ? [] : HiddenOfferData({offerData: offer?.offerFields, allOffers})
                    ?.filter?.(x=>!x?.hide || (x?.clientOrder?._ref && x?.clientOrder?._ref != clientOrder?._id)))?.flat()
                    ?.every?.((x)=>x?.clientOrder?._ref)
                    //console.log("lishcnilsdcndsilucn", areAllOffersOrdered)
                    
                  const iconsData = {
                    ...dataFilter({allGeneratedOfferData, registerTemplateId, siteId: "allSites", clientOrder}),
                    clientOrder,
                    areAllFamiliesHidden,
                    areAllOffersHidden,
                    areAllFamiliesOrdered: headerTitle == "PLESSO" ? false : areAllFamiliesOrdered,
                    areAllOffersOrdered,
                    isThereSignRegister,
                    registersWithRegisterTemplateId
                  }
                  const selectFamily = (e) =>{
                    //#showHideOffer
                    e.stopPropagation();
                    if(!Array.isArray(allOffers)){
                      errorAlert("ERRORE: ALL OFFERS NON E' UN ARRAY")
                      return
                    }
                    if(allOffers?.length < 1){
                      errorAlert("ERRORE: ALL OFFERS E' VUOTO")
                      return
                    }
                    if(headerTitle !== "PLESSO") {
                      if(areAllFamiliesHidden) {
                        console.log("SHOW FAMILY", {registersWithRegisterTemplateId, headerTitle, registerTemplateId})
                        const showFamily = !Array.isArray(allOffers) ? [] : allOffers?.map?.((offer)=>({...offer, hideRegisters: offer?.hideRegisters?.filter?.((x)=> !registersWithRegisterTemplateId?.some?.((y)=>y?._id == x))}))
                        showHide(showFamily)
                        return
                      }
                      const hideFamily = !Array.isArray(allOffers) ? [] : allOffers?.map?.((offer)=>({...offer, hideRegisters: [...!Array.isArray(offer?.hideRegisters) ? [] : offer?.hideRegisters, ...registersWithRegisterTemplateId?.map?.((x)=>x?._id)] }))
                      showHide(hideFamily)
                      console.log("HIDE FAMILY", {registersWithRegisterTemplateId, headerTitle, registerTemplateId})
                    } else {
                      if(areAllOffersHidden) {
                        console.log("SHOW ALL OFFERS")
                        const showAllOffers = !Array.isArray(allOffers) ? [] : allOffers?.map?.((offer)=>({...offer, hideRegisters: []}))
                        showHide(showAllOffers)
                        return
                      }
                      const allHiddenOffers = !Array.isArray(allOffers) ? [] : allOffers?.map?.((offer)=>({...offer, hideRegisters: [...!Array.isArray(existingRegistersIds) ? [] : existingRegistersIds]}))
                      showHide(allHiddenOffers)
                      console.log("HIDE ALL OFFERS")

                    }
 
                  }
                  const orderFamily = (e) =>{
                    //#showHideOrder
                    //Manages the cart icons and orders on the table header, !!IT OVERRIDES SINGLE LINE ORDERS
                    e.stopPropagation();
                    if(!Array.isArray(allOffers)){
                      errorAlert("ERRORE: ALL OFFERS NON E' UN ARRAY")
                      return
                    }
                    if(allOffers?.length < 1){
                      errorAlert("ERRORE: ALL OFFERS E' VUOTO")
                      return
                    }
                    if(headerTitle !== "PLESSO") {
                        // If header is "PLESSO" it would order the whole offer, not the family, which is below
                        if(areAllFamiliesOrdered) {
                          const deleteOrderInAllFamilies = allOffers?.map?.((offer)=>({
                            ...offer, 
                            offerFields: !Array.isArray(HiddenOfferData({offerData: offer?.offerFields, allOffers})) ? [] : 
                              HiddenOfferData({offerData: offer?.offerFields, allOffers})
                            ?.filter((x)=>!x?.hide)
                            ?.map?.((item)=>
                              { 
                                const findOriginalItem =!Array.isArray(offer?.offerFields) ? {} : offer?.offerFields?.find?.((x)=>x?._id == item?._id)
                                if(item?.hide) return findOriginalItem
                                if(registerTemplateIdWithSigns(item) != registerTemplateId) return findOriginalItem
                                if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return findOriginalItem
                                return({...item, clientOrder: undefined})
                              })
                            })
                          )
                          console.log("DELETE ORDER IN ALL FAMILY", deleteOrderInAllFamilies)
                          showHide(deleteOrderInAllFamilies)
                          return
                          }
                      const orderInAllFamilies = allOffers?.map?.((offer)=>({
                        ...offer, 
                        offerFields: !Array.isArray(HiddenOfferData({offerData: offer?.offerFields, allOffers})) ? [] : 
                          HiddenOfferData({offerData: offer?.offerFields, allOffers})
                        ?.filter((x)=>!x?.hide)
                        ?.map?.((item)=>
                          { 
                            const findOriginalItem = !Array.isArray(offer?.offerFields) ? {} : offer?.offerFields?.find?.((x)=>x?._id == item?._id)
                            if(item?.hide) return findOriginalItem
                            if(registerTemplateIdWithSigns(item) != registerTemplateId) return findOriginalItem
                            if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return item
                            return({...findOriginalItem, clientOrder: referenceDoc(clientOrder?._id)})
                          })
                      })
                    )
                      console.log("ORDER ALL ORDERS IN FAMILY", orderInAllFamilies)
                      showHide(orderInAllFamilies)
                      return
                    } else {
                      //manages if the user clicks the top left cart icon to order or delete order in all offers. !!IT OVERRIDES SINGLE LINE ORDER
                      if(areAllOffersOrdered) {
                        const deleteOrderInAllOffers = allOffers?.map?.((offer)=>({
                            ...offer, 
                            offerFields: !Array.isArray(HiddenOfferData({offerData: offer?.offerFields, allOffers})) ? [] : HiddenOfferData({offerData: offer?.offerFields, allOffers})
                            ?.filter((x)=>!x?.hide)
                            ?.map?.((item)=>
                              { 
                                const findOriginalItem = !Array.isArray(offer?.offerFields) ? {} : offer?.offerFields?.find?.((x)=>x?._id == item?._id)
                                if(item?.hide) return findOriginalItem
                                if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return findOriginalItem
                                return({...findOriginalItem, clientOrder: undefined})
                              })
                          })
                        )
                        console.log("DELETE ORDER IN ALL OFFERS", deleteOrderInAllOffers)
                        showHide(deleteOrderInAllOffers)
                        return
                      }                      
                      const orderAllOffers =  allOffers?.map?.((offer)=>({
                        ...offer, 
                        offerFields: !Array.isArray(HiddenOfferData({offerData: offer?.offerFields, allOffers})) ? [] :
                        HiddenOfferData({offerData: offer?.offerFields, allOffers})
                        ?.filter((x)=>!x?.hide)
                        ?.map?.((item)=>
                            {  
                              const findOriginalItem = !Array.isArray(offer?.offerFields) ? {} : offer?.offerFields?.find?.((x)=>x?._id == item?._id)
                              if(item?.hide) { return findOriginalItem }
                              if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return findOriginalItem
                              return({...findOriginalItem, clientOrder:  referenceDoc(clientOrder?._id)})
                            })
                      })
                    )
                      console.log("ORDER ALL OFFERS", orderAllOffers)
                      showHide(orderAllOffers)
                    }
                  }
                  const buttonTitle = () =>{
                    if(headerTitle == "PLESSO") {
                      return "sel tutto"
                    } 
                    return "sel registri"

                  }
                  return (
                  <th key={index}{...column.getHeaderProps(column.getSortByToggleProps())} style={{...totalsBackground, fontSize:"12px"}} >
                    <div style={{display:"flex", alignItems:"center"}}>
                      {/* <button onClick={()=>console.log(iconsData)} >test icon</button> */}
                      <VisDiv onClick={orderFamily} rules={[clientOrder, headerTitle !== "TOTALI", !areAllFamiliesHidden, !areAllOffersHidden]}>
                        <CartIcon data={iconsData}/>
                      </VisDiv>
                      <VisDiv  onClick={selectFamily} rules={[!clientOrder, offer, headerTitle !== "TOTALI"]}>
                        <EyeIcon data={iconsData}/>
                      </VisDiv>
                      <div style={{marginLeft:"4px"}}>
                        {headerTitle}
                      </div>
                        
                      <span>
                        {column.isSorted ? (column.isSortedDesc ? " ⬆️" : " ⬇️"):""}
                      </span>
                    </div>

                  </th>
                )})}
              </tr>
            ))} 
          </thead>
          <tbody {...getTableBodyProps()} style={{fontSize:"12px"}}>
            {rows.map?.((row, rowIndex) => {
              const rowColour = rowIndex % 2 ? "white":"#f3f4f3"
              prepareRow(row);
              
              return (
                <Fragment key={rowIndex}>
                  <tr style={{backgroundColor: rowColour}} {...row.getRowProps()}  >
                    {row.cells.map?.((cell, cellIndex) => {
                      
                      const row = cell?.row
                      //console.log("celllll",cell)
                      const totalCellColor = () => {
                        if(cell?.column?.Header == "TOTALI") {
                          const backColor = rowIndex % 2 ? "#c2c0c0" : "#A5A5A5"
                          return ({backgroundColor: backColor, color: "white", fontWeight:'bold', fontSize:"14px"})
                        }
                        return ({})
                      }

                      const registerTemplateId = cell?.column?.id == "signs" ? "weduwheiudghyegwuyjke88363ghdloiejdf87" : cell?.column?.id
                      const foundSiteName = cell?.row?.values?.site
                      const site = !Array.isArray(sites) ? undefined : sites?.find?.(x=> foundSiteName == siteName(x))
                      const siteOffer = !Array.isArray(allOffers) ? undefined : allOffers?.find?.(x=>x?.site?._ref == site?._id)                     
  

                      // these are all the register ids in the offer fields including sign "weduwheiudghyegwuyjke88363ghdloiejdf87"
                      const existingRegistersIds = registerIdsFromOfferFields(siteOffer?.offerFields) 
                      const isThereSignRegister = existingRegistersIds?.some?.((x=>x=="weduwheiudghyegwuyjke88363ghdloiejdf87"))
                      //console.log("kwndkjwnckwejcnakwejnc", registers)
                    // const registersWithRegisterTemplateId =!Array.isArray(registers) ? [] : registers?.filter?.((x)=>x?.template_ref == registerTemplateId)
                    const registersWithRegisterTemplateId =!Array.isArray(registers) ? [] : 
                    isThereSignRegister && registerTemplateId == "weduwheiudghyegwuyjke88363ghdloiejdf87" ? 
                      [  {
                          label: "SEGNALETICA",
                          site:{_type:"reference", _ref: site?._id},
                          _id: "weduwheiudghyegwuyjke88363ghdloiejdf87",
                          _type: "register",
                          status: "ATTIVO", 
                          template:{_type:"reference",  _ref: "weduwheiudghyegwuyjke88363ghdloiejdf87"},
                      }]
                      : registers?.filter?.((x)=>x?.template?._ref == registerTemplateId)
                  //console.log("lsclsdcjsdljc signs",{registerTemplateId, registersWithRegisterTemplateId, existingRegistersIds, offerFields: offerData})

                      
                      const hideRegisters = !Array.isArray(siteOffer?.hideRegisters) ? [] : siteOffer?.hideRegisters
                      //const isRegisterHidden = hideRegisters?.some?.(x => x == registerTemplateId)
                      const isRegisterHidden = allArrayDataInTargetArray({sourceArray: hideRegisters, targetArray: registersWithRegisterTemplateId?.map?.((x)=>x?._id)}) 
                      const isSiteHidden = 
                        !Array.isArray(siteOffer?.offerFields) ? false : 
                         allArrayDataInTargetArray({sourceArray: siteOffer?.hideRegisters, targetArray: existingRegistersIds})


                      const isSiteOrdered = !Array.isArray(siteOffer?.offerFields) ? false : HiddenOfferData({offerData: siteOffer?.offerFields, allOffers:[siteOffer]})
                        ?.filter?.(x=>!x?.hide || (x?.clientOrder?._ref && x?.clientOrder?._ref != clientOrder?._id))
                        ?.every?.(x=>x?.clientOrder?._ref)
                      const isRegisterOrdered = !Array.isArray(siteOffer?.offerFields) ? false : HiddenOfferData({offerData: siteOffer?.offerFields, allOffers:[siteOffer]})
                        ?.filter?.(x=>(registerTemplateIdWithSigns(x) == registerTemplateId && !x?.hide) || (x?.clientOrder?._ref && x?.clientOrder?._ref != clientOrder?._id))
                        ?.every?.(x=>x?.clientOrder?._ref)
                      const iconsData = {
                        ...dataFilter({allGeneratedOfferData, registerTemplateId, siteId: site?._id, clientOrder}),
                        isRegisterHidden,
                        isSiteHidden,
                        isSiteOrdered,
                        isRegisterOrdered: registerTemplateId == "site" ? false : isRegisterOrdered
                      }
                      const clickCell = () => {
                        //onCellClick (site, register, registerTemplate)
                        //console.log("cell", cell)
                        console.log("bksdhcbsdkhbc", cell)
                      }
                      const selectRegister = (e) =>{
                        //#showHideOffer
                        e.stopPropagation();
                        if(!siteOffer || !siteOffer?._id) {
                          console.log("NO OFFER FOUND")
                          return
                        }
                          //showHide(data)
                          if(registerTemplateId !== "site") {
                            if(isRegisterHidden) {
                              const toggleHideRegister = {
                                ...siteOffer,
                                hideRegisters: hideRegisters?.filter?.(x=>!registersWithRegisterTemplateId?.some?.((y)=>y?._id == x))
                              }
                              console.log("SHOW REGISTER", {toggleHideRegister, registerTemplateId, registersWithRegisterTemplateId})
                              showHide([toggleHideRegister])
                            } else {
                              const toggleHideRegister = {
                                ...siteOffer,
                                hideRegisters: [...hideRegisters, ...registersWithRegisterTemplateId?.map?.((x)=>x?._id)]
                              }
                              console.log("HIDE REGISTER", {toggleHideRegister, registerTemplateId, registersWithRegisterTemplateId })
                              showHide([toggleHideRegister])
                            } 
                            
                          } 
                          else {
                            const siteRegistersIds = registerIdsFromOfferFields(siteOffer?.offerFields)
                            if(isSiteHidden) {
                              const toggleShowSiteOffer = {
                                ...siteOffer,
                                hideRegisters: []
                              }
                              console.log("SHOW SITE", toggleShowSiteOffer)
                              showHide([toggleShowSiteOffer])
                              return
                            }
                              const toggleShowSiteOffer = {
                                ...siteOffer,
                                hideRegisters: [...siteRegistersIds]
                              }
                              console.log("HIDE SITE", toggleShowSiteOffer)
                              showHide([toggleShowSiteOffer])
                            //console.log("SITE OFFER", siteOffer)
                          }
                      }
                      const orderRegister = (e) => {
                        //#showHideOrder
                        e.stopPropagation();
                        if(!siteOffer || !siteOffer?._id) {
                          console.log("NO OFFER FOUND")
                          return
                        }
                        if(registerTemplateId !== "site") {
                          //manages ordering the single register family
                          if(isRegisterOrdered) {
                            console.log("DELETE ORDER REGISTER")
                            const updatedOffer = {
                              ...siteOffer, 
                              offerFields: !Array.isArray(HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})) ? [] :
                              HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})
                              ?.filter((x)=>!x?.hide)
                              ?.map?.((item)=>
                                  { 
                                    const findOriginalItem = !Array.isArray(siteOffer?.offerFields) ? {} : siteOffer?.offerFields?.find?.((x)=>x?._id == item?._id)
                                    if(item?.hide) { return findOriginalItem }
                                    if(registerTemplateIdWithSigns(item) != registerTemplateId) return findOriginalItem
                                    if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return findOriginalItem
                                    return({...findOriginalItem, clientOrder:  undefined})
                                  })
                            }
                            showHide([updatedOffer])
                            return
                          } else {
                            console.log("ORDER REGISTER")
                            const updatedOffer = {
                              ...siteOffer, 
                              offerFields: !Array.isArray(HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})) ? [] :
                              HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})
                              ?.filter((x)=>!x?.hide)
                              ?.map?.((item)=>
                                  { 
                                    const findOriginalItem = !Array.isArray(siteOffer?.offerFields) ? {} : siteOffer?.offerFields?.find?.((x)=>x?._id == item?._id)
                                    if(item?.hide) { return findOriginalItem }
                                    if(registerTemplateIdWithSigns(item) != registerTemplateId) return findOriginalItem
                                    if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return findOriginalItem
                                    return({...findOriginalItem, clientOrder:  referenceDoc(clientOrder?._id)})
                                  })
                            }
                            showHide([updatedOffer])
                            return
                          }  
                        } 
                        else {
                            //manages ordering the whole site
                          if(isSiteOrdered){
                            console.log("DELETE ORDER SITE")
                            const updatedOffer = {
                              ...siteOffer, 
                              offerFields: !Array.isArray(HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})) ? [] :
                              HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})
                              ?.filter((x)=>!x?.hide)
                              ?.map?.((item)=>
                                  { 
                                    const findOriginalItem = !Array.isArray(siteOffer?.offerFields) ? {} : siteOffer?.offerFields?.find?.((x)=>x?._id == item?._id)
                                    if(item?.hide) { return findOriginalItem }
                                    if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return findOriginalItem
                                    return({...findOriginalItem, clientOrder:  undefined})
                                  })
                            }
                            showHide([updatedOffer])
                            return

                          } else {
                            console.log("ORDER SITE")
                            const updatedOffer = {
                              ...siteOffer, 
                              offerFields: !Array.isArray(HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})) ? [] :
                              HiddenOfferData({offerData: siteOffer?.offerFields, allOffers})
                              ?.filter((x)=>!x?.hide)
                              ?.map?.((item)=>
                                  { 
                                    const findOriginalItem = !Array.isArray(siteOffer?.offerFields) ? {} : siteOffer?.offerFields?.find?.((x)=>x?._id == item?._id)
                                    if(item?.hide) { return findOriginalItem }
                                    if(item?.clientOrder?._ref && item?.clientOrder?._ref != clientOrder?._id) return findOriginalItem
                                    return({...findOriginalItem, clientOrder: referenceDoc(clientOrder?._id)})
                                  })
                            }
                            showHide([updatedOffer])

                          }
                        }
                      }
                      const thisOrderData = iconsData?.filteredData?.filter?.((x)=>x?.clientOrder?._ref == clientOrder?._id)
                      const thisOrderRegisterTotal = +offerTotals(thisOrderData)?.priceToClient

                      const orderValue = () => {
                       /* if(isRegisterOrdered) {
                           return cell?.value
                        }
                        if(isSiteOrdered) {
                          return cell?.value
                        } */
                        if(thisOrderData?.length > 0) {
                          return thisOrderRegisterTotal
                        }
                        //return cell?.value
                      }
                      return(
                      <td onClick={clickCell} key={cellIndex} style={{...totalCellColor(), borderRight: 1, borderRightColor: "#d1c5c5", borderRightStyle: "dotted"}}  {...cell.getCellProps()}> 
                        <div style={{display:"flex", alignItems:"center"}}>
                          <VisDiv rules={[registerTemplateId !== "totals", offer]}>
                            <VisDiv onClick={orderRegister} rules={[clientOrder, !isRegisterHidden, !isSiteHidden]}>
                              <CartIcon data={iconsData}/>
                            </VisDiv>
                            <VisDiv onClick={selectRegister} rules={[!clientOrder]}>
                              <EyeIcon data={iconsData}/>
                            </VisDiv>
                          </VisDiv>
                          <VisDiv rules={[clientOrder]} style={{marginLeft:"4px"}}>
                            <VisDiv rules={[orderValue(), cell?.column?.Header !== "PLESSO"]}>
                              {`(${formattedValue(cell?.column?.Header, orderValue())})`}
                            </VisDiv>
                            <div>
                              {formattedValue(cell?.column?.Header, cell?.value)}
                            </div>
{/*                             <button onClick={()=>console.log("oqk,dpoqwkdopw",{iconsData, thisOrderData,thisOrderToal: thisOrderRegisterTotal})} >test ord icon</button>
 */}                        <VisDiv rules={[iconsData?.isRegisterHidden, iconsData?.filteredData?.length > 0]}>
                              {euro(0)}
                            </VisDiv>
                          </VisDiv>
                           <VisDiv rules={[!clientOrder]} style={{marginLeft:"4px"}}>
                            {formattedValue(cell?.column?.Header, cell?.value)}
                            {/* <button onClick={()=>console.log(iconsData)} >test reg icon</button> */}
                            <VisDiv rules={[iconsData?.isRegisterHidden, iconsData?.filteredData?.length > 0]}>
                              {euro(0)}
                            </VisDiv>
                          </VisDiv>
                         
                        </div>
                       </td>
                    )})}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
          {footerGroups?.map?.((footerGroup, index) => (
            <tr key={index} {...footerGroup?.getFooterGroupProps()}>
              {footerGroup?.headers.map?.((column, index) => {
                //console.log("Footer", column)
                const background = column?.Header == "TOTALI" ? {backgroundColor:"black"}:{backgroundColor:"grey"}
                const clickFooterCell = () => {
                  console.log("òkjanwòvfjn", column)
                }
                return(
                <td onClick={clickFooterCell} key={index} {...column?.getHeaderProps} style={{...background, color:"white", textAlign:"left", fontWeight:'bold', fontSize:"14px", borderRight: 1, borderRightColor: "#d1c5c5", borderRightStyle: "dotted"}}>
                  
                    {formattedValue(column?.Header, column?.render("Footer"))}
                  
                </td>
              )}
              )}
            </tr>
          ))}
        </tfoot>
        </table>
      </div>
      <Divider bottom={"10px"}/>
    </div>
  )
}

export default OfferSummary


/**
 * siteList is a function that returns an array of objects, each containing data for a site.
 * The objects have the following properties:
 * - Header: the label of the site
 * - accessor: the id of the site
 * - value: the value of the site (either the number of devices to supply, the number of devices not checked, or the price to the client)
 * - siteNumber: the site number of the site
 * - Footer: the total price to the client of all the sites
 *
 * The function takes four parameters:
 * - data: an object containing the data for the sites
 * - workingOfferData: an array of objects containing the data for the non conformities
 * - dataType: an object containing the label and value of the data type to be used
 *
 * The function filters the non conformities data by the site id and the data type, and then calculates the total price to the client of all the sites.
 *
 * @param {Object} data - an object containing the data for the sites
 * @param {Array} workingOfferData - an array of objects containing the data for the non conformities
 * @param {Object} dataType - an object containing the label and value of the data type to be used
 * @returns {Array} an array of objects, each containing data for a site
 */
export const siteList = (data={}, workingOfferData=[], dataType={label:"PREZZO", value:"PREZZO"}) => 
//#showHideOffer
//#showHideOrder
{
const {devices=[], sites, registers, nonConformities, registerTemplates, allOffers, clientOrder} = data
const filterOtherOrders = (x) => {
  if(!clientOrder) return x
  if(!clientOrder?._ref) return x
  return x?.clientOrder?._ref == clientOrder?._id
}
const filterCurrentOrder = (x) => {
  //if(x?.clientOrder?._ref)console.log("òaoerjvòajjfnvòa", x)
  if(!clientOrder) return x
  return x?.clientOrder?._ref == clientOrder?._id
}
const hiddenWorkingOfferData = (Array.isArray(allOffers) && allOffers?.length > 0 && Array.isArray(workingOfferData)) ? 
    HiddenOfferData({offerData: workingOfferData, allOffers}) : 
      !Array.isArray(workingOfferData) ? [] : 
      workingOfferData

return (sites?.map?.((site)=>{
  
    const siteRegisters = !registers ? [] : registers?.filter?.(x=>site?._id == x?.site?._ref).map?.((register)=>{

      const registerTemplate = !Array.isArray(registerTemplates) ? undefined : registerTemplates?.find?.((y)=>register?.template._ref == y?._id)

      const registerDevices = !Array.isArray(devices) ? [] : devices?.filter?.(x=>x?.register?._ref == register?._id && x?.status !== "ARCHIVIATO" && x?.status !== "DA FORNIRE").map?.((device)=>{
          const nonConformitiesInDevice = !Array.isArray(device?.nonConformities) ? [] : device?.nonConformities?.filter?.(x=>x?.status !== "ARCHIVIATO" &&  x?.status !== "COMPLETATO")
          const isSupply = nonConformitiesInDevice?.some?.((z)=>
              { 
                const nonConfTemplate = !Array.isArray(nonConformities) ? undefined : nonConformities?.find?.(x=>x?._id == z.nonConformity?._ref)
                if(nonConfTemplate?.supply == true) return true
                return false
              })

          if(isSupply) return undefined
          return device
          //return device
          
        }).filter?.(y=>y)
        //console.log("lawbhlawhbdvc", workingOfferData)
        //const priceToClient = registerDevices?.length
        const filterOfferData = hiddenWorkingOfferData
          ?.filter?.((x)=>x?.registerId == register?._id && !x?.isSign)
          ?.filter?.(filterOtherOrders)
        //#offerTotals
        const priceToClient = +offerTotals(filterOfferData)?.priceToClient?.toFixed(2)
        const priceToClientFooter = +offerTotals(filterOfferData?.filter?.(filterCurrentOrder))?.priceToClient?.toFixed(2)
       


        const nDevicesToSupply = !Array.isArray(devices) ? 0 : devices?.filter?.(x=>x?.register?._ref == register?._id && x?.status == "DA FORNIRE").length
        const nDevicesNotChecked = !Array.isArray(devices) ? 0 : devices?.filter?.(x=>x?.register?._ref == register?._id && !isDeviceChecked(x, register)).length

        const value = () => {

          if(dataType?.value == "MARGINE") return nDevicesToSupply
          if(dataType?.value == "COSTO") return nDevicesNotChecked

          return priceToClient
        }  
           //#offerTotals
           //console.log("wjkednkwjdnwkejnd",{ Header:register?.label, accessor: register?.template?._ref, value: value(), Footer: priceToClientFooter } )

        return (
            { Header:register?.label, accessor: register?.template?._ref, value: value(), Footer: priceToClientFooter }
        )
    })
    /*     const totals1 = () => {
      return (siteRegisters?.map?.((x)=>+x?.value)?.reduce((partialSum, a) => partialSum + a, 0)
      +  +offerTotals(filterSignsData)?.priceToClient
      )?.toFixed(2) 
    } */
    //#offerTotals
    //TOTALS
    //SIGNS
    const filterSignsData = hiddenWorkingOfferData
      ?.filter?.((x)=>x?.siteId == site?._id && x?.isSign)
      ?.filter?.(filterOtherOrders)  
    const filterRegistersData = hiddenWorkingOfferData
      ?.filter?.((x)=>x?.siteId == site?._id && !x?.isSign)
      ?.filter?.(filterOtherOrders)
    const registersPriceToClientFooter = +offerTotals(filterRegistersData?.filter?.(filterCurrentOrder))?.priceToClient?.toFixed(2)
    const signsPriceToClientFooter = +offerTotals(filterSignsData?.filter?.(filterCurrentOrder))?.priceToClient?.toFixed(2)
    const signsPriceToClient = +offerTotals(filterSignsData)?.priceToClient?.toFixed(2)
    const totals = () => registersPriceToClientFooter + signsPriceToClientFooter
    return (
        [
            {Header:"PLESSO", accessor: "site", value: siteName(site), siteNumber: site?.siteNumber, Footer: 'TOTALI'},
            ...siteRegisters,
            {Header:"SEGNALETICA", accessor: "signs", value: signsPriceToClient, siteNumber: site?.siteNumber, Footer: signsPriceToClientFooter },
            {Header:"TOTALI", accessor: "totals", value: totals(), siteNumber: site?.siteNumber, Footer: totals() }
        ]
    )
})
)}
//==============

/**
 * A component that displays a legend for a chart or table.
 * @param {Object} props - The props object.
 * @param {string} props.description - The description of the legend.
 * @param {string} props.color - The color of the legend.
 * @param {string} props.label - The label of the legend.
 * @returns {React.ReactElement} - A React element representing the legend.
 */
export const Legenda = ({description="", color="", label=""}) => {
    return(

             <div style={{display:"flex", marginTop:"15px", alignItems:"center"}}>
                <div style={{marginRight:"10px"}}><TextHighlight highlight={color}>{label}</TextHighlight></div>
                <p>{description}</p>
            </div>

    )
}


/**
 * A function that takes registerTemplates and workingData as input and returns a table object.
 * The table object contains columns and data.
 * The columns are an array of objects containing accessor, Header, siteNumber, order, and Footer.
 * The data is an array of objects containing the values for each column.
 * @param {Array} registerTemplates - An array of objects containing the register templates.
 * @param {Array} workingData - An array of objects containing the working data.
 * @returns {Object} - A table object containing columns and data.
 */
export const summaryTableData = (registerTemplates, workingData) => {
  //console.log("lawejnflwEKNFLiwn", workingData)
  const tableValues = !workingData ? [] : workingData?.map?.((x)=> x?.reduce((prevItem, item)=>{
    const exists = !!prevItem[item.accessor]
    const value = () => {
      if (exists) {return prevItem[item.accessor]+item.value}
      return item.value
    }
    return({...prevItem, [item.accessor]: value()})
  },{})
  )

  
  const uniqueTableDataArray = unique("accessor",workingData?.flat(2))

  const tableColumns = uniqueTableDataArray.map?.((item => {
    const order = !Array.isArray(registerTemplates) ? undefined : registerTemplates?.find?.(x=>x._id==item?.accessor)?.order
    const values = workingData?.flat(2)?.filter?.((x)=>x?.accessor == item?.accessor)?.map?.((item)=>+item?.value)
    const FooterValues = workingData?.flat(2)?.filter?.((x)=>x?.accessor == item?.accessor)?.map?.((item)=>Number(item?.Footer) ? item?.Footer : 0)
    const sum = FooterValues?.reduce((partialSum, a) => partialSum + a, 0)?.toFixed(2)
    const footerTotal = () => {
      //if(sum && item?.accessor == "totals") return `TOTALE COMPLESSIVO: ${+sum}`
      if(sum) return sum 
      return 0
    } 
    return({
      accessor: item?.accessor,
      Header: item?.Header,
      siteNumber: item?.siteNumber,
      order: order,
      Footer:item?.Footer == "TOTALI" ? item?.Footer : footerTotal()

  })}))

  return ({
      columns: tableColumns,
      data: tableValues,
      })

}

/**
 * A function that takes allGeneratedOfferData, registerTemplateId, siteId, and clientOrder as input and returns an object containing filteredData, allProductsHidden, and allProductsOrdered.
 * filteredData is an array of objects containing the filtered data.
 * allProductsHidden is a boolean indicating whether all products are hidden.
 * allProductsOrdered is a boolean indicating whether all products have been ordered.
 * @param {Array} allGeneratedOfferData - An array of objects containing the generated offer data.
 * @param {string} registerTemplateId - The id of the register template.
 * @param {string} siteId - The id of the site.
 * @param {Object} clientOrder - The client order object.
 * @returns {Object} - An object containing filteredData, allProductsHidden, and allProductsOrdered.
 */
export const dataFilter = ({allGeneratedOfferData, registerTemplateId, siteId, clientOrder}) => {
  const filter = (x) => {
    if(registerTemplateId == "site" && siteId == "allSites") return x
    if(registerTemplateId == "site" && siteId) return x?.siteId == siteId
    if(registerTemplateId == "weduwheiudghyegwuyjke88363ghdloiejdf87" && siteId == "allSites" ) return x?.isSign
    if(registerTemplateId && siteId == "allSites") return x?.registerTemplateId == registerTemplateId && !x?.isSign
    if(registerTemplateId == "weduwheiudghyegwuyjke88363ghdloiejdf87" && siteId) return x?.siteId == siteId && x?.isSign
    if(registerTemplateId && siteId) return x?.registerTemplateId == registerTemplateId && x?.siteId == siteId && !x?.isSign
  }
  const clientOrderFilter = (x) => {
    if(!clientOrder) return x
    return ((x?.clientOrder?._ref == clientOrder?._id || !x?.clientOrder) && !x?.hide )
  }
  const filteredData = !Array.isArray(allGeneratedOfferData) ? [] : allGeneratedOfferData?.filter?.(filter)?.filter?.(clientOrderFilter)
  const allProductsHidden = !Array.isArray(filteredData) ?  false : filteredData?.every?.((x)=>x?.hide)
  const allProductsOrdered = !Array.isArray(filteredData) ?  false : filteredData?.every?.((x)=>x?.clientOrder)
  return ({filteredData, allProductsHidden, allProductsOrdered})
}


/**
 * A component that displays an eye icon to toggle visibility of offer items
 * 
 * @param {Object} props - Component props
 * @param {Array} props.generatedOfferData - Array of generated offer data to check visibility status
 * @param {string} props.registerTemplateId - ID of the register template for filtering data
 * @param {string} props.siteId - ID of the site for filtering data
 * @returns {JSX.Element} An eye icon that toggles between visible/hidden states based on data
 */
export const EyeIcon = ({data}) => {
  //#showHideOffer
  //const data = dataFilter({allGeneratedOfferData, registerTemplateId, siteId})
  const {filteredData, allProductsHidden, areAllFamiliesHidden, areAllOffersHidden, isRegisterHidden, isSiteHidden} = data
 //console.log("lzmc.scnkl<sjcnkl<sjndc", areAllFamiliesHidden)
  const visibility = () => {
      if(!Array.isArray(filteredData)) return "noData"
      //this below does not allow me to hide/show offered data 
      //if(filteredData?.filter?.(x=>!x?.clientOrder)?.length < 1) return "noData"

      //if(allLabourProducts && total == 0) return "noData"
      //if(productsVisible?.length > 0 && allVisibleLabourProducts && allVisibleTotal == 0) return "noData"
      if(allProductsHidden || areAllFamiliesHidden || areAllOffersHidden || isRegisterHidden || isSiteHidden) return "hidden"
      return "visible"
  }
  //const allProductsHidden = false
  const showSite = () => {
    console.log("sedhweudh", {data, filteredData, noOrderData: filteredData?.filter?.(x=>!x?.clientOrder)})
  }
  const hideSite = () => {
    console.log("dweidfhawiehfu",  {data, filteredData, noOrderData: filteredData?.filter?.(x=>!x?.clientOrder)})
  }
  return (
    <VisDiv rules={[true]} style={{color:"darkBlue", cursor:"pointer", marginLeft:"10px"}}>
      <VisDiv className='round-back-button' style={{backgroundColor:"grey", width: "20px", height: "20px"}} rules={[visibility() == "hidden"]} onClick={(e) => {showSite()}}>
        <IoIosEyeOff fontSize={15} color='white'/>
      </VisDiv>
      <VisDiv className='round-back-button' style={{backgroundColor:"green", width: "20px", height: "20px"}} rules={[visibility() == "visible"]} onClick={(e) => {hideSite()}}>
        <IoIosEye fontSize={15} color='white' />
      </VisDiv>
      <VisDiv rules={[visibility() == "noData"]} style={{marginLeft:"10px"}}> 0 </VisDiv>
    </VisDiv>
  )
}

/**
 * A component that displays an cart icon to toggle visibility of offer items
 * 
 * @param {Object} props - Component props
 * @param {Array} props.generatedOfferData - Array of generated offer data to check visibility status
 * @param {string} props.registerTemplateId - ID of the register template for filtering data
 * @param {string} props.siteId - ID of the site for filtering data
 * @returns {JSX.Element} An eye icon that toggles between visible/hidden states based on data
 */
export const CartIcon = ({data}) => {
  //#showHideOrder
  //const data = dataFilter({allGeneratedOfferData, registerTemplateId, siteId, clientOrder})
  const {filteredData, allProductsOrdered, isSiteOrdered, isRegisterOrdered, areAllFamiliesOrdered, areAllOffersOrdered} = data

  const visibility1 = () => {
      if(filteredData?.length < 1) return "noData"
      //if(allLabourProducts && total == 0) return "noData"
      //if(productsVisible?.length > 0 && allVisibleLabourProducts && allVisibleTotal == 0) return "noData"
      if(allProductsOrdered) return "allOrdered"
      return "notAllOrdered"
  }
  const visibility = () => {
    if(!Array.isArray(filteredData)) return "noData"
    if(filteredData?.length < 1) return "noData"
    //if(allLabourProducts && total == 0) return "noData"
    //if(productsVisible?.length > 0 && allVisibleLabourProducts && allVisibleTotal == 0) return "noData"
    if(allProductsOrdered || isSiteOrdered || isRegisterOrdered || areAllFamiliesOrdered || areAllOffersOrdered) return "allOrdered"
    return "notAllOrdered"
}
  //const allProductsHidden = false
  const showSite = () => {
    //console.log("foòijqoòqjedòfi", {filteredData, allProductsOrdered, registerTemplateId, siteId})
  }
  const hideSite = () => {
    //console.log("qe2fjoòi2hjeiufh",  {filteredData,  allProductsOrdered, registerTemplateId, siteId})
  }
  return (
    <VisDiv rules={[true]} style={{color:"darkBlue", cursor:"pointer", marginLeft:"10px"}}>
      <VisDiv className='round-back-button' style={{backgroundColor:"green", width: "20px", height: "20px"}} rules={[visibility() == "allOrdered"]} onClick={(e) => {showSite()}}>
        <IoMdCart fontSize={15} color='white'/>
      </VisDiv>
      <VisDiv className='round-back-button' style={{backgroundColor:"grey", width: "20px", height: "20px"}} rules={[visibility() == "notAllOrdered"]} onClick={(e) => {hideSite()}}>
        <IoMdCart fontSize={15} color='white' />
      </VisDiv>
      <VisDiv rules={[visibility() == "noData"]} style={{marginLeft:"10px"}}></VisDiv>
    </VisDiv>
  )
}
